import { Component, computed, OnInit, Signal, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { Method } from '@bo/ng-api';
import { RegisterService } from '../../../../_services/register-service/register.service';
import { BoFormsModule } from '@bo/ng-forms';
import { PasswordFieldComponent } from '../../../../shared/components/forms/password-field/password-field.component';
import { SubmitComponent } from '../../../../shared/components/forms/submit/submit.component';
import { ValidTokenData, VerifyAccountData, VerifyAccountResponse } from '../../../../_interfaces/verify-account';
import { RouterSwitchBtnComponent } from '../../../../shared/components/router-switch-btn/router-switch-btn.component';
import { SetAppStateFromSignUpPasswordSet } from '../../../../store/app/app.actions';
import {
  BaseCreatePasswordComponent,
  ErrorDetail,
  Params,
} from '../../../../shared/components/create-password/create-password.component';
import { LoaderComponent } from '../../../../shared/components/loader/loader.component';

@Component({
  selector: 'app-create-password-sign-up',
  imports: [BoFormsModule, PasswordFieldComponent, SubmitComponent, RouterSwitchBtnComponent, LoaderComponent],
  templateUrl: '../../../../shared/components/create-password/create-password.component.html',
})
export class CreatePasswordSignUpComponent
  extends BaseCreatePasswordComponent<VerifyAccountResponse>
  implements OnInit
{
  override readonly store: Store;
  readonly router = inject(Router);
  readonly registerService = inject(RegisterService);
  readonly route = inject(ActivatedRoute);

  override returnBtnCallback = this.backToGetStarted;
  override returnBtnLabel = 'Get started';
  override formLabel: string = 'signUpCreatePassword';

  override invalidDetail: ErrorDetail = {
    title: '   We had a problem validating your email address...',
    text: [
      'The email link might have expired. Please click the button below. We will send you a new link to verify your account.',
    ],
  };
  override notFoundDetail: ErrorDetail = {
    title: "We couldn't find your account.",
    text: [
      'If you have already created a password, you can proceed to login to your account.',
      'Please follow the link below to create a new account.',
    ],
  };

  readonly params: Signal<Params> = computed(() => {
    const queryParams = this.route.snapshot.queryParams;
    return {
      code: queryParams['code'] as string,
      id: queryParams['signup_id'] as string,
    };
  });
  resend$!: Observable<VerifyAccountResponse>;

  constructor() {
    const store = inject(Store);
    super();
    this.store = store;
  }

  override ngOnInit(): void {
    super.ngOnInit();

    // Create the resend$ observable after params are available
    this.resend$ = this.registerService.resendEmail(this.params().id);
    const data: ValidTokenData = { code: this.params().code };
    this.registerService.verifyAccountTokenValid(this.params().id, data).subscribe(this.validateResponseHandler());
  }

  override createForm(): void {
    const URL_SIGN_UP = this.registerService.URL_SIGNUP;
    const URL_VERIFY = this.registerService.URL_VERIFY;
    this.buildFromOptions(
      this.registerService.getOptions(`${URL_SIGN_UP}/${this.params().id}/${URL_VERIFY}/`, Method.POST),
    ).subscribe();
  }

  override handleSubmitSuccess(res: VerifyAccountResponse): void {
    this.store.dispatch(new SetAppStateFromSignUpPasswordSet(res)).subscribe({
      next: () => {
        this.router.navigate(['sign-up', 'verify-phone']);
      },
    });
  }

  override submitData(formData: VerifyAccountData): Observable<VerifyAccountResponse> {
    formData.code = this.params().code;
    return this.registerService.verifyAccount(this.params().id, formData);
  }

  backToGetStarted(): void {
    this.router.navigate(['sign-up', 'get-started']);
  }
}
